import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Mail, 
  Bell, 
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AlertsSettings() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [config, setConfig] = useState({
    whatsapp_alert_number: '',
    email_alert_address: '',
    alerts_enabled: true,
    alert_on_new_reservation: true,
    alert_on_cancellation: true,
    alert_on_no_show: true,
    alert_on_high_risk: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/restaurants/alerts-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Error fetching alerts config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/restaurants/alerts-config`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (res.ok) {
        toast.success('Configuración de alertas guardada');
      } else {
        toast.error('Error al guardar configuración');
      }
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Master Switch */}
      <Card data-testid="alerts-master-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Alertas del Sistema</CardTitle>
                <CardDescription>Recibe notificaciones importantes sobre tu restaurante</CardDescription>
              </div>
            </div>
            <Switch
              checked={config.alerts_enabled}
              onCheckedChange={(checked) => setConfig({ ...config, alerts_enabled: checked })}
              data-testid="alerts-enabled-switch"
            />
          </div>
        </CardHeader>
      </Card>

      {config.alerts_enabled && (
        <>
          {/* Contact Methods */}
          <Card data-testid="alerts-contact-card">
            <CardHeader>
              <CardTitle className="text-base">Canales de Notificación</CardTitle>
              <CardDescription>Configura dónde quieres recibir las alertas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* WhatsApp */}
              <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-900">
                <div className="h-10 w-10 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-green-800 dark:text-green-200 font-medium">
                    Número de WhatsApp
                  </Label>
                  <Input
                    type="tel"
                    placeholder="+52 55 1234 5678"
                    value={config.whatsapp_alert_number}
                    onChange={(e) => setConfig({ ...config, whatsapp_alert_number: e.target.value })}
                    className="bg-white dark:bg-background"
                    data-testid="whatsapp-number-input"
                  />
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Incluye el código de país. Las alertas llegarán a este número.
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-200 dark:border-blue-900">
                <div className="h-10 w-10 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-blue-800 dark:text-blue-200 font-medium">
                    Correo Electrónico
                  </Label>
                  <Input
                    type="email"
                    placeholder="alertas@mirestaurante.com"
                    value={config.email_alert_address}
                    onChange={(e) => setConfig({ ...config, email_alert_address: e.target.value })}
                    className="bg-white dark:bg-background"
                    data-testid="email-alert-input"
                  />
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Puedes usar un correo diferente al de la cuenta principal.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alert Types */}
          <Card data-testid="alerts-types-card">
            <CardHeader>
              <CardTitle className="text-base">Tipos de Alertas</CardTitle>
              <CardDescription>Selecciona qué eventos generan notificaciones</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* New Reservation */}
              <div className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium text-sm">Nueva Reservación</p>
                    <p className="text-xs text-muted-foreground">Cuando un cliente hace una reservación</p>
                  </div>
                </div>
                <Switch
                  checked={config.alert_on_new_reservation}
                  onCheckedChange={(checked) => setConfig({ ...config, alert_on_new_reservation: checked })}
                  data-testid="alert-new-reservation-switch"
                />
              </div>

              {/* Cancellation */}
              <div className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-medium text-sm">Cancelación</p>
                    <p className="text-xs text-muted-foreground">Cuando una reservación es cancelada</p>
                  </div>
                </div>
                <Switch
                  checked={config.alert_on_cancellation}
                  onCheckedChange={(checked) => setConfig({ ...config, alert_on_cancellation: checked })}
                  data-testid="alert-cancellation-switch"
                />
              </div>

              {/* No Show */}
              <div className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="font-medium text-sm">No Show</p>
                    <p className="text-xs text-muted-foreground">Cuando un cliente no se presenta</p>
                  </div>
                </div>
                <Switch
                  checked={config.alert_on_no_show}
                  onCheckedChange={(checked) => setConfig({ ...config, alert_on_no_show: checked })}
                  data-testid="alert-no-show-switch"
                />
              </div>

              {/* High Risk */}
              <div className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="font-medium text-sm">Alto Riesgo de No-Show</p>
                    <p className="text-xs text-muted-foreground">Cuando se detecta una reservación de alto riesgo (ML)</p>
                  </div>
                </div>
                <Switch
                  checked={config.alert_on_high_risk}
                  onCheckedChange={(checked) => setConfig({ ...config, alert_on_high_risk: checked })}
                  data-testid="alert-high-risk-switch"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Save Button */}
      <Button 
        onClick={handleSave} 
        disabled={saving}
        className="w-full rounded-xl"
        data-testid="save-alerts-btn"
      >
        {saving ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Save className="h-4 w-4 mr-2" />
        )}
        Guardar Configuración de Alertas
      </Button>
    </div>
  );
}
